import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  defaultLocale,
  type Locale,
} from "../../i18n/translations";
import { translate, type TranslationParams } from "../../i18n/translate";
import { setUiLocale } from "./runtime";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, fallback: string, params?: TranslationParams) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({
  initialLocale,
  children,
}: {
  initialLocale?: Locale;
  children: React.ReactNode;
}) => {
  const [locale, setLocale] = useState<Locale>(initialLocale ?? defaultLocale);
  useEffect(() => {
    setUiLocale(locale);
  }, [locale]);
  const setLocaleWithRuntime = useCallback((nextLocale: Locale) => {
    setUiLocale(nextLocale);
    setLocale(nextLocale);
  }, []);
  const t = useCallback(
    (key: string, fallback: string, params?: TranslationParams) =>
      translate(locale, key, fallback, params),
    [locale],
  );
  const value = useMemo(
    () => ({ locale, setLocale: setLocaleWithRuntime, t }),
    [locale, setLocaleWithRuntime, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
};
